import { SuggestionData } from 'ckeditor5-premium-features'
import { useSuggestionsStore } from 'components/WorkspaceEditor/SuggestionStore'
import { TrackedChange } from 'models/changes'
import VirtualList, { ListRef } from 'rc-virtual-list'
import React, { memo, useRef } from 'react'
import { SuggestionCardWrapper } from '../SuggestionCardWrapper'
import { useContainerHeight } from './hooks/useConatinerHeight'
import { useHighlightVirtualizedCard } from './hooks/useHighlightVirtualizedCard'
import { useResetScrollOnRequest } from './hooks/useResetScrollOnRequest'

interface VirtualizedSuggestionListProps {
    suggestions: SuggestionData[]
    trackChangesData: Record<string, TrackedChange>
    containerHeight?: number
    isCustomFilterActive?: boolean
}

const DEFAULT_CONTAINER_HEIGHT = 1000
const HEIGHT_BUFFER = 90
const ITEM_HEIGHT = 90

/**
 * VirtualizedSuggestionList component renders a list of suggestions
 * @param {VirtualizedSuggestionListProps} props - The properties for the component.
 * @return {JSX.Element} The rendered virtualized suggestion list.
 */
export const VirtualizedSuggestionList = memo(
    ({
        suggestions,
        trackChangesData,
        isCustomFilterActive = false,
    }: VirtualizedSuggestionListProps) => {
        const listRef = useRef<ListRef>(null)
        const activeCardFilter = useSuggestionsStore(
            (s) => s.suggestionCardFilter
        )

        const containerHeight = useContainerHeight(
            '[data-testid="tree-layout"]',
            DEFAULT_CONTAINER_HEIGHT
        )
        const viewportHeight = containerHeight + HEIGHT_BUFFER

        useHighlightVirtualizedCard(suggestions, listRef)
        useResetScrollOnRequest(listRef, isCustomFilterActive)

        return (
            <VirtualList
                data={suggestions}
                height={viewportHeight}
                itemHeight={ITEM_HEIGHT}
                itemKey="id"
                virtual
                ref={listRef}
            >
                {(suggestion: SuggestionData, index: number) => (
                    <div key={suggestion.id}>
                        <SuggestionCardWrapper
                            suggestion={suggestion}
                            trackChangesData={trackChangesData}
                            index={index}
                            isNotDefaultSuggestionCardFilter={
                                isCustomFilterActive
                            }
                            suggestionCardFilter={activeCardFilter}
                        />
                    </div>
                )}
            </VirtualList>
        )
    }
)
